import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import SearchSelect from "@/components/common/SearchSelect";
import { fmtDate, fmtINR, today } from "@/lib/format";
import { toast } from "sonner";
import { FileMinus, Truck, Download } from "lucide-react";

export default function PurchaseReturn() {
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [returns, setReturns] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({
    against_bill_id: "", vendor_id: "", date: today(),
    reason: "quality_reject", amount_pct: 100, notes: "",
    regenerate_ewb: true, vehicle_no: "",
  });
  const navigate = useNavigate();

  const load = async () => {
    setBills((await api.get("/purchase/bills")).data);
    setVendors((await api.get("/parties?party_type=vendor")).data);
    setReturns((await api.get("/debit-notes")).data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.against_bill_id) return toast.error("Pick the original bill");
    if (!(form.amount_pct > 0 && form.amount_pct <= 100)) return toast.error("Percent 1-100");
    try {
      const r = await api.post(`/purchases/bills/${form.against_bill_id}/return`, form);
      toast.success(`Debit Note ${r.data.note_number} posted · ${fmtINR(r.data.grand_total)} reversed${r.data.ewb_no ? ` · EWB ${r.data.ewb_no}` : ""}`);
      setOpenNew(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="purchase-return-page">
      <PageHeader eyebrow="Procurement" title="Purchase Return"
        description="Return goods to vendors with a GST-compliant Debit Note + auto-regenerated Part-A e-way bill on the return leg."
        actions={<Button className="rounded-sm bg-accent text-white" onClick={() => setOpenNew(true)} data-testid="new-return-btn">+ New Return</Button>}
      />
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Date</th><th>Note #</th><th>Vendor</th><th>Against Bill</th><th className="text-right">Amount</th><th>Reason</th><th>EWB</th></tr></thead>
          <tbody data-testid="return-list">
            {returns.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">No purchase returns yet</td></tr>}
            {returns.map((r) => (
              <tr key={r.id}>
                <td className="text-xs">{fmtDate(r.date)}</td>
                <td className="font-mono text-xs">{r.note_number}</td>
                <td>{r.vendor_name}</td>
                <td className="font-mono text-xs">{r.against_bill_number || "-"}</td>
                <td className="text-right tabular-nums">{fmtINR(r.grand_total)}</td>
                <td className="text-xs">{r.reason}</td>
                <td className="font-mono text-xs">{r.ewb_no || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openNew && (
        <Card className="p-5 rounded-sm max-w-3xl fixed inset-0 m-auto z-50 h-fit shadow-2xl border-2 border-accent" data-testid="new-return-dialog">
          <div className="text-[10px] uppercase font-mono text-accent tracking-widest">Create Purchase Return</div>
          <div className="text-lg font-semibold mt-1 mb-4">Debit Note against a Purchase Bill</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Against Bill</Label>
              <SearchSelect
                options={bills.map((b) => ({ value: b.id, label: `${b.bill_number} · ${b.vendor_name} · ${fmtINR(b.grand_total)}`, sublabel: fmtDate(b.date) }))}
                value={form.against_bill_id}
                onChange={(v) => { const b = bills.find((x) => x.id === v); setForm({ ...form, against_bill_id: v, vendor_id: b?.vendor_id || "" }); }}
                testId="ret-bill" placeholder="Select purchase bill" />
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" data-testid="ret-date" /></div>
            <div><Label>Reverse %</Label><Input type="number" min="1" max="100" value={form.amount_pct} onChange={(e) => setForm({ ...form, amount_pct: Number(e.target.value) })} className="rounded-sm mt-1 text-right tabular-nums" data-testid="ret-pct" /></div>
            <div><Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="ret-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quality_reject">Quality Rejection</SelectItem>
                  <SelectItem value="short_shipment">Short Shipment</SelectItem>
                  <SelectItem value="damage">Damaged in Transit</SelectItem>
                  <SelectItem value="wrong_item">Wrong Item</SelectItem>
                  <SelectItem value="price_correction">Price Correction</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 border rounded-sm p-2 bg-muted/30">
              <input type="checkbox" checked={form.regenerate_ewb} onChange={(e) => setForm({ ...form, regenerate_ewb: e.target.checked })} data-testid="ret-ewb-toggle" />
              <div>
                <div className="text-sm font-medium">Regenerate e-Way Bill</div>
                <div className="text-[10px] text-muted-foreground">Part-A for the return leg (vehicle already required if ≥ ₹50k)</div>
              </div>
            </div>
            {form.regenerate_ewb && (
              <div><Label>Vehicle No.</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" placeholder="MH01AB1234" data-testid="ret-vehicle" /></div>
            )}
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1 min-h-[60px]" data-testid="ret-notes" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="rounded-sm" onClick={() => setOpenNew(false)}>Cancel</Button>
            <Button className="rounded-sm bg-accent text-white" onClick={submit} data-testid="ret-submit">Post Return</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
